import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { W_WorkOrderReport_UnassignedEntries_VM } from 'ab-api/web/workOrderReport/timeCardUnassignEntriesSidePanel';

import { abbreviateTimeZone } from '@acceligentllc/shared/utils/time';

import type { RootState } from 'af-reducers';

import LoadingIndicator from 'af-components/LoadingIndicator';

import styles from './styles.module.scss';
import UnassignedEntry from './UnassignedEntry';

interface EntryIdWorkOrderIdCombination {
	[entryId: string]: number;
}

interface OwnProps {
	accountId: number;
	entryIdListForDeletion: number[];
	shouldRefresh: boolean;
	fetchUnassignedEntriesFromAccount: (accountId: number) => Promise<W_WorkOrderReport_UnassignedEntries_VM[]>;
	setEntryWorkOrderCombination: (entryIdWoIdObj: EntryIdWorkOrderIdCombination) => void;
	setEntryIdListForDeletion: (list: number[]) => void;
	setShouldRefresh: (shouldRefresh: boolean) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const UnassignedEntriesWrapper: React.FC<Props> = (props) => {
	const { accountId,
		entryIdListForDeletion,
		shouldRefresh,
		fetchUnassignedEntriesFromAccount,
		setEntryWorkOrderCombination,
		setEntryIdListForDeletion,
		setShouldRefresh,
		companyData,
	} = props;

	const [isLoading, setIsLoading] = React.useState(false);
	const [accountUnassignedEntries, setAccountUnassignedEntries] = React.useState<W_WorkOrderReport_UnassignedEntries_VM[]>([]);

	function removeEntryWithId(entryList: W_WorkOrderReport_UnassignedEntries_VM[], id: number) {
		return entryList.filter((obj) => obj.id !== id);
	}

	const removeEntryFromListWithoutSaving = React.useCallback((entryId: number) => {
		const listWithRemovedEntry = removeEntryWithId(accountUnassignedEntries, entryId);
		setAccountUnassignedEntries(listWithRemovedEntry);
		setEntryIdListForDeletion([...entryIdListForDeletion, entryId]);
	}, [accountUnassignedEntries, entryIdListForDeletion, setEntryIdListForDeletion]);

	const fetchAndSetData = React.useCallback(async () => {
		setIsLoading(true);
		const unassignedEntries = await fetchUnassignedEntriesFromAccount(accountId);
		setIsLoading(false);
		setAccountUnassignedEntries(unassignedEntries);
	}, [accountId, fetchUnassignedEntriesFromAccount]);

	const renderUnassignedEntries = React.useCallback((_unassigneedEntry: W_WorkOrderReport_UnassignedEntries_VM) => {
		return (
			<React.Fragment key={_unassigneedEntry.id}>
				<UnassignedEntry
					removeEntry={removeEntryFromListWithoutSaving}
					setEntryWorkOrderCombination={setEntryWorkOrderCombination}
					unassignedEntry={_unassigneedEntry}
				/>
			</React.Fragment>
		);
	}, [removeEntryFromListWithoutSaving, setEntryWorkOrderCombination]);

	React.useEffect(() => {
		fetchAndSetData();
		setShouldRefresh(false);
	}, [fetchAndSetData, accountId, setShouldRefresh, shouldRefresh]);

	if (isLoading) {
		return (
			<div className={styles['side-panel__loading']}>
				<LoadingIndicator color="orange" />
			</div>
		);
	}

	return (
		<>
			<div className="time-sheet-list__time-zone">The company's designated time zone is used. ({abbreviateTimeZone(companyData.timezone, 'z (Z)')})</div>
			<div className={styles.panelMessage}>Leaving pane view without saving will reset all changes made.</div>
			{!!accountUnassignedEntries.length && accountUnassignedEntries.map((_unassigneedEntry) => {
				return renderUnassignedEntries(_unassigneedEntry);
			})}
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyData,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(UnassignedEntriesWrapper);
